import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Car } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { MileageSheetEditor } from './MileageSheetEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const isApprover = (u) => !!u && (u.is_superadmin || ['admin', 'superadmin', 'manager'].includes(u.role));
export const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700', submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-700',
};
const thisMonth = () => new Date().toISOString().slice(0, 7);

const PickSelect = ({ value, onChange, items, placeholder, testId, searchable }) => {
  const [q, setQ] = useState('');
  const shown = q ? items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase()) || i.id === value) : items;
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {searchable && (
          <div className="p-1" onKeyDown={(e) => e.stopPropagation()}>
            <Input autoFocus className="h-8 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder="…" data-testid={`${testId}-search`} />
          </div>
        )}
        {shown.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const NewSheetDialog = ({ open, onClose, assetId, projectId, onCreated }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState({ asset_id: assetId || '', project_id: projectId || '', month: thisMonth(), driver_employee_id: '' });
  const [opts, setOpts] = useState({ vehicles: [], projects: [], employees: [] });
  useEffect(() => {
    if (!open) return;
    setForm({ asset_id: assetId || '', project_id: projectId || '', month: thisMonth(), driver_employee_id: '' });
    Promise.all([
      axios.get(`${API}/mileage-sheets/vehicles`).catch(() => ({ data: [] })),
      axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      isApprover(user) ? axios.get(`${API}/employees`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]).then(([v, p, e]) => setOpts({
      vehicles: v.data.map((x) => ({ id: x.id, label: `${x.name} · ${x.registration}${x.ownership === 'employee_owned' ? ` (${t('mileage.employeeCar')})` : ''}` })),
      projects: p.data.map((x) => ({ id: x.id, label: [x.project_number, x.name].filter(Boolean).join(' · ') })),
      employees: e.data.map((x) => ({ id: x.id, label: x.name || [x.first_name, x.last_name].filter(Boolean).join(' ') })),
    }));
  }, [open, assetId, projectId, user, t]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    try {
      const r = await axios.post(`${API}/mileage-sheets`, { ...form, driver_employee_id: form.driver_employee_id || null });
      toast.success(t('mileage.created'));
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t('mileage.failed')); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="mileage-new-dialog">
        <DialogHeader><DialogTitle>{t('mileage.newSheet')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!assetId && <PickSelect value={form.asset_id} onChange={(v) => set('asset_id', v)} items={opts.vehicles} placeholder={t('mileage.vehicle')} testId="mileage-new-vehicle" />}
          {opts.vehicles.length === 0 && !assetId && <p className="text-xs text-amber-700">{t('mileage.noVehicles')}</p>}
          {!projectId && <PickSelect value={form.project_id} onChange={(v) => set('project_id', v)} items={opts.projects} placeholder={t('mileage.project')} testId="mileage-new-project" searchable />}
          {isApprover(user) && <PickSelect value={form.driver_employee_id} onChange={(v) => set('driver_employee_id', v)} items={opts.employees} placeholder={t('mileage.driverMe')} testId="mileage-new-driver" searchable />}
          <Input type="month" value={form.month} onChange={(e) => set('month', e.target.value)} data-testid="mileage-new-month" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={create} disabled={!form.asset_id || !form.project_id || !form.month} data-testid="mileage-new-create">{t('mileage.create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Monthly mileage sheets, optionally limited to one car or one project. */
export const MileageSheets = ({ assetId, projectId }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mileage-sheets`, { params: { asset_id: assetId, project_id: projectId } });
      setItems(r.data);
    } catch { setItems([]); }
  }, [assetId, projectId]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card data-testid="mileage-sheets-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base"><Car className="h-4 w-4" />{t('mileage.title')}</CardTitle>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="mileage-new-btn"><Plus className="h-4 w-4 mr-1" />{t('mileage.newSheet')}</Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="mileage-empty">{t('mileage.empty')}</p> : (
          <ul className="divide-y">
            {items.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => setOpenId(s.id)} className="flex w-full flex-wrap items-center justify-between gap-2 py-2 text-left hover:bg-slate-50" data-testid={`mileage-row-${s.id}`}>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{s.month} · {s.asset_name} {s.registration}</span>
                    <span className="block truncate text-xs text-slate-500">{s.driver_name} · {s.project_name}</span>
                  </span>
                  <span className="flex items-center gap-2 text-xs">
                    <span className="whitespace-nowrap">{t('mileage.kmAmount', { km: s.total_km, amount: s.amount.toFixed(2) })}</span>
                    <Badge className={STATUS_TONE[s.status]}>{t(`mileage.status.${s.status}`)}</Badge>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <NewSheetDialog open={creating} onClose={() => setCreating(false)} assetId={assetId} projectId={projectId}
        onCreated={(s) => { setCreating(false); load(); setOpenId(s.id); }} />
      {openId && <MileageSheetEditor sheetId={openId} onChanged={load} onClose={() => { setOpenId(null); load(); }} />}
    </Card>
  );
};

export default MileageSheets;
